import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Download, ChevronLeft, ChevronRight } from 'lucide-react';

const API = '/api';
const EVENT_LABELS = {
  LOGIN_SUCCESS: 'Login',
  LOGIN_FAILURE: 'Login Failed',
  LOGOUT: 'Logout',
  PASSWORD_RESET_REQUEST: 'Password Reset',
  PASSWORD_CHANGED: 'Password Changed',
  ACCOUNT_LOCKED: 'Account Locked',
};

function parseBrowser(ua) {
  if (!ua) return 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('python-requests')) return 'API Client';
  if (ua.includes('curl')) return 'cURL';
  return ua.substring(0, 30);
}

export default function LoginAuditTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ user: '', event_type: '', outcome: '', date_from: '', date_to: '' });

  const loadAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('page_size', 50);
      if (filters.user) params.set('user', filters.user);
      if (filters.event_type) params.set('event_type', filters.event_type);
      if (filters.outcome) params.set('outcome', filters.outcome);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const { data } = await axios.get(`${API}/admin/login-audit?${params}`, { withCredentials: true });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      console.error('Failed to load audit log:', e);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (filters.user) params.set('user', filters.user);
    if (filters.event_type) params.set('event_type', filters.event_type);
    if (filters.outcome) params.set('outcome', filters.outcome);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    try {
      const res = await axios.get(`${API}/admin/login-audit/export?${params}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `login_audit_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    }
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="login-audit-tab">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>Login Audit Trail</h2>
          <Button variant="outline" size="sm" onClick={handleExport} className="border-slate-300 text-slate-700" data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="flex flex-wrap gap-3">
          <Input value={filters.user} onChange={e => { setFilters(f => ({ ...f, user: e.target.value })); setPage(1); }}
            placeholder="Search user/email..." className="w-48 rounded-lg" data-testid="audit-filter-user" />
          <Select value={filters.event_type} onValueChange={v => { setFilters(f => ({ ...f, event_type: v === 'all' ? '' : v })); setPage(1); }}>
            <SelectTrigger className="w-44 rounded-lg" data-testid="audit-filter-event"><SelectValue placeholder="All Events" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="LOGIN_SUCCESS">Login Success</SelectItem>
              <SelectItem value="LOGIN_FAILURE">Login Failure</SelectItem>
              <SelectItem value="LOGOUT">Logout</SelectItem>
              <SelectItem value="PASSWORD_RESET_REQUEST">Password Reset</SelectItem>
              <SelectItem value="PASSWORD_CHANGED">Password Changed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.outcome} onValueChange={v => { setFilters(f => ({ ...f, outcome: v === 'all' ? '' : v })); setPage(1); }}>
            <SelectTrigger className="w-36 rounded-lg" data-testid="audit-filter-outcome"><SelectValue placeholder="All Outcomes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="failure">Failure</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={filters.date_from} onChange={e => { setFilters(f => ({ ...f, date_from: e.target.value })); setPage(1); }}
            className="w-40 rounded-lg" data-testid="audit-filter-from" />
          <Input type="date" value={filters.date_to} onChange={e => { setFilters(f => ({ ...f, date_to: e.target.value })); setPage(1); }}
            className="w-40 rounded-lg" data-testid="audit-filter-to" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="audit-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">User / Email</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Event</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Outcome</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">IP Address</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Device / Browser</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No audit records found</td></tr>
            ) : items.map(item => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                  {new Date(item.timestamp).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-slate-900">{item.email_attempted}</td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                    {EVENT_LABELS[item.event_type] || item.event_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${item.outcome === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {item.outcome}
                  </span>
                  {item.failure_reason && (
                    <span className="block text-[11px] text-red-500 mt-0.5">{item.failure_reason}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 font-mono text-xs">{item.ip_address}</td>
                <td className="px-4 py-3 text-sm text-slate-600 text-xs">{parseBrowser(item.user_agent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <span className="text-sm text-slate-500">{total} records — Page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
